import { useEffect, useMemo, useState } from 'react';
import { BREAKPOINTS } from '~/constants';

type TBreakpointKey = keyof typeof BREAKPOINTS;

type TUseMediaQueryOptions = {
  defaultValue?: boolean;
  initializeWithValue?: boolean;
};

const IS_SERVER = typeof window === 'undefined';

export function useMediaQuery<T extends readonly TBreakpointKey[]>(
  breakpoints: T,
  { defaultValue = false, initializeWithValue = true }: TUseMediaQueryOptions = {},
): { [K in keyof T]: boolean } {
  const breakpointsKey = breakpoints.join(',');

  const queries = useMemo(
    () => breakpoints.map((bp) => BREAKPOINTS[bp]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [breakpointsKey],
  );

  const getMatches = (query: string): boolean => {
    if (IS_SERVER) return defaultValue;
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState<boolean[]>(() => {
    if (!initializeWithValue) {
      return queries.map(() => defaultValue);
    }
    return queries.map(getMatches);
  });

  useEffect(() => {
    if (IS_SERVER) return;

    const mediaQueryLists = queries.map((q) => window.matchMedia(q));

    const handleChange = () => {
      setMatches(mediaQueryLists.map((mql) => mql.matches));
    };

    handleChange();

    mediaQueryLists.forEach((mql) => {
      mql.addEventListener('change', handleChange);
    });

    return () => {
      mediaQueryLists.forEach((mql) => {
        mql.removeEventListener('change', handleChange);
      });
    };
  }, [queries]);

  return matches as { [K in keyof T]: boolean };
}
